import React, { useEffect, useRef, useState } from "react";
import { IoBagHandleOutline } from "react-icons/io5";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { addToCart } from "../../api/cartApi";
import { getProducts, PRODUCT_SORT_OPTIONS } from "../../api/productApi";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const PAGE_SIZE = 12;
const SEARCH_DEBOUNCE_MS = 400;
const DEFAULT_SORT = PRODUCT_SORT_OPTIONS[0].value;

const ShopCardList = ({ sellerId: sellerIdProp } = {}) => {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [onOffer, setOnOffer] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const requestIdRef = useRef(0);

  // 🔹 Use the sellerId passed in as a prop (e.g. from a profile's Shop tab) if given,
  // otherwise fall back to the URL (this page's own standalone ?sellerId= usage).
  const queryParams = new URLSearchParams(location.search);
  const sellerId = sellerIdProp ?? queryParams.get("sellerId");

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Every filter change reloads page 1; "Load more" appends the next page with the same filters
  const loadProducts = (pageNum) => {
    const requestId = ++requestIdRef.current;
    const filters = { seller_id: Number(sellerId), sortBy };
    if (categoryId) filters.category_id = Number(categoryId);
    if (onOffer) filters.on_offer = true;
    if (search) filters.search = search;

    setLoading(true);
    setError(null);
    getProducts(filters, pageNum, PAGE_SIZE)
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        if (data?.success && Array.isArray(data.result?.products)) {
          setProducts((prev) => (pageNum === 1 ? data.result.products : [...prev, ...data.result.products]));
          setTotal(data.result.total || 0);
          setPage(pageNum);
        } else {
          setError(data?.message || "Failed to load products.");
        }
      })
      .catch(() => {
        if (requestId === requestIdRef.current) setError("Something went wrong while fetching products.");
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  };

  useEffect(() => {
    if (!Number(sellerId)) {
      setError("Seller ID not provided in the URL.");
      return;
    }
    loadProducts(1);
  }, [sellerId, categoryId, onOffer, search, sortBy]);

  useEffect(() => {
    const token = sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken");
    if (!token) return;
    fetch(`${API_BASE_URL}/product_category/get-categories`, {
      method: "POST",
      headers: { "Content-Type": "application/json", token },
      body: JSON.stringify({ filters: {}, page: 1, limit: 100 }),
    })
      .then((response) => response.json())
      .then((data) => {
        const categoriesData = data?.result?.categories || data?.result?.ProductCategories;
        setCategories(data?.success && Array.isArray(categoriesData) ? categoriesData : []);
      })
      .catch(() => setCategories([]));
  }, []);

  const isFiltered = Boolean(categoryId || onOffer || search);

  const resetFilters = () => {
    setCategoryId("");
    setOnOffer(false);
    setSearchInput("");
    setSearch("");
    setSortBy(DEFAULT_SORT);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Filters */}
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div className="flex flex-wrap gap-3">
          <select
            className="border px-4 py-2 rounded-full"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.title}
              </option>
            ))}
          </select>
          <select
            className="border px-4 py-2 rounded-full"
            value={onOffer ? "offers" : ""}
            onChange={(e) => setOnOffer(e.target.value === "offers")}
          >
            <option value="">All Products</option>
            <option value="offers">On Offer</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-3">
          <input
            type="search"
            value={searchInput}
            maxLength={100}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search products"
            className="border px-4 py-2 rounded-full"
          />
          <select
            className="border px-4 py-2 rounded-full"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            {PRODUCT_SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {(isFiltered || searchInput || sortBy !== DEFAULT_SORT) && (
            <button
              type="button"
              onClick={resetFilters}
              className="border border-[#095228] text-[#095228] px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#EBF8ED]"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>

      {error ? (
        <div className="p-6 text-red-600">{error}</div>
      ) : loading && page === 1 && !products.length ? (
        <div className="p-6 text-gray-600">Loading products...</div>
      ) : !products.length ? (
        <div className="p-6 text-gray-600">
          {isFiltered ? "No products match these filters." : "No products found."}
        </div>
      ) : (
        <>
          <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 ${loading ? "opacity-60" : ""}`}>
            {products.map((product) => (
              <ProductCard product={product} key={product.id} />
            ))}
          </div>
          {products.length < total && (
            <div className="flex justify-center mt-6">
              <button
                type="button"
                disabled={loading}
                onClick={() => loadProducts(page + 1)}
                className="px-6 py-2 rounded-full border border-gray-900 text-sm font-semibold disabled:opacity-60"
              >
                {loading ? "Loading..." : "Load more"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ShopCardList;

function ProductCard({ product }) {
  const navigate = useNavigate();
  const [addingToCart, setAddingToCart] = useState(false);
  const price = Number(product.product_price) || 0;
  const salePrice = Number(product.discount_price) || price;
  const currencySymbol = product.seller?.currency_symbol || "$";
  const offerPercentage = salePrice < price ? Math.round(((price - salePrice) / price) * 100) : 0;

  const openProduct = () => {
    navigate(`/ProductView/${product.id}`, {
      state: { productId: product.id },
    });
  };

  const handleAddToCart = async () => {
    if (addingToCart) return;
    setAddingToCart(true);
    try {
      const res = await addToCart(product.id, 1, 0);
      if (res?.success) {
        toast.success(res.message || "Added to cart");
      } else {
        toast.error(res?.message || "Failed to add to cart");
      }
    } catch (err) {
      toast.error("Failed to add to cart");
    } finally {
      setAddingToCart(false);
    }
  };

  return (
    <div className="p-2">
      <div className="relative bg-[#FAFAFA] rounded-lg shadow-sm cursor-pointer" onClick={openProduct}>
        <div className="w-full h-[170px]">
          <img
            src={product.product_media?.[0]?.url || "/image/shoeimg.png"}
            alt={product.product_name}
            className="w-full h-full object-contain rounded-t-lg"
          />
        </div>

        {offerPercentage > 0 && (
          <span className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded">
            {offerPercentage}% Offer
          </span>
        )}
      </div>

      <p
        className="mt-4 text-sm text-gray-600 uppercase line-clamp-2 cursor-pointer hover:underline"
        onClick={openProduct}
      >
        {product.product_name}
      </p>

      <div className="flex justify-between items-center">
        <div className="flex flex-col">
          {offerPercentage > 0 && (
            <span className="line-through text-gray-400 text-sm">{currencySymbol}{price.toFixed(2)}</span>
          )}
          <span className="text-black font-semibold">{currencySymbol}{salePrice.toFixed(2)}</span>
        </div>

        <button
          type="button"
          onClick={handleAddToCart}
          disabled={addingToCart || product.inStock === false}
          className="mt-3 flex py-2 px-3 text-[14px] gap-2 items-center bg-gray-900 text-white rounded-full disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <IoBagHandleOutline className="text-lg" />
          {product.inStock === false ? "OUT OF STOCK" : addingToCart ? "ADDING..." : "ADD TO CART"}
        </button>
      </div>
    </div>
  );
}
